import React, { useState, useEffect } from "react";
import {
  Typography,
  Box,
  Container,
  CssBaseline,
  Paper,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import Navbar from "../sidebar/SideNavbar";
import AuthApi4 from "../services/authApi";
import moment from "moment";
import * as XLSX from "xlsx";

export default function WalletHistory() {
  const navigate = useNavigate();

  const [reportData, setReportData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [filterType, setFilterType] = useState(""); // "deposit" or "deduction"

  useEffect(() => {
    getItems();
  }, []);

  useEffect(() => {
    setFilteredData(
      reportData.filter((row) => {
        const matchesSearch =
          (row.No_of_days
            ? moment(row.No_of_days).format("YYYY-MM-DD HH:mm:ss").toLowerCase()
            : ""
          ).includes(searchTerm.toLowerCase()) ||
          (row.stb_number?.toString() || "")
            .toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          (row.No_of_STB?.toString() || "")
            .toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          (row.user_name?.toString() || "")
            .toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          (row.deposit?.toString() || "")
            .toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          (row.balance?.toString() || "")
            .toLowerCase()
            .includes(searchTerm.toLowerCase());

        const matchesFilter =
          filterType === "deposit"
            ? row.deposit > 0
            : filterType === "deduction"
            ? row.user_name > 0
            : true;

        return matchesSearch && matchesFilter;
      })
    );
  }, [searchTerm, reportData, filterType]);

  const getItems = async () => {
    try {
      const data = {
        records: [
          {
            sub_oid: parseInt(localStorage.getItem("payment_cust_num")),
            type: "REACT_CUSTOMER",
          },
        ],
      };
      const response = await AuthApi4.renewReport(data);
      const plans = response.data.re;
      const planslist = plans
        .sort((a, b) => a.No_of_days - b.No_of_days)
        .map((plan) => ({
          No_of_days: plan.insertion_time,
          stb_number: plan.stb_number,
          No_of_STB: plan.tran_details,
          user_name: plan.deduction,
          deposit: plan.deposit,
          balance: plan.balance,
        }));

      setReportData(planslist);
      setFilteredData(planslist);
    } catch (e) {
      console.error(e);
    }
  };

  const handleSearchClick = async () => {
    const data = {
      records: [
        {
          operator_id: parseInt(localStorage.getItem("ownerId")),
          phone: localStorage.getItem("phoneNumber"),
          from_date: startDate,
          to_date: endDate,
          type: "Filter1",
          deduct_deposit: filterType,
          sub_oid: parseInt(localStorage.getItem("payment_cust_num")),
        },
      ],
    };

    try {
      const response = await AuthApi4.renewReport(data);
      const resData = response.data.re || [];
      const planslist = resData.map((plan) => ({
        No_of_days: plan.insertion_time,
        stb_number: plan.stb_number,
        No_of_STB: plan.tran_details || plan.tran_details,
        user_name: plan.deduction,
        deposit: plan.deposit,
        balance: plan.balance,
      }));

      setReportData(planslist);
      setFilteredData(planslist);
    } catch (e) {
      console.log("Renewal Report API Error", e);
    }
  };

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchTermChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  // Excel Export Function
  const handleExportToExcel = () => {
    const exportData = filteredData.map((row, index) => ({
      "SL NO": index + 1,
      DATE: moment.utc(row.No_of_days).format("YYYY-MM-DD HH:mm:ss"),
      "STB NUMBER": row.stb_number,
      "TRANSACTION DETAILS": row.No_of_STB,
      DEDUCTION: row.user_name,
      DEPOSIT: row.deposit,
      "WALLET BALANCE": row.balance,
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Wallet History");
    XLSX.writeFile(workbook, "WalletHistory.xlsx");
  };

  return (
    <div style={{ display: "flex", background: "#F5F5F5" }}>
      <CssBaseline />
      <Navbar />
      <main style={{ flexGrow: 1, padding: "5px", minHeight: "100vh" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            paddingTop: 10,
          }}
        >
          <Typography variant="h5">Wallet History</Typography>
          <TextField
            label="Search"
            variant="outlined"
            value={searchTerm}
            onChange={handleSearchTermChange}
            size="small"
            sx={{ marginRight: 2, maxWidth: 200 }}
          />
        </Box>

        {/* Filters + Get Report + Export */}
        <Box
          sx={{
            display: "flex",
            gap: 2,
            alignItems: "center",
            justifyContent: "center",
            paddingY: 2,
          }}
        >
          <TextField
            label="Start Date"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
          />
          <TextField
            label="End Date"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
          />
          <FormControl size="small">
  <InputLabel>Mode of Transaction</InputLabel>
  <Select
    value={filterType}
    label="Transaction Type"
    onChange={(e) => setFilterType(e.target.value)}
    sx={{ minWidth: 220 }} // increase width here
  >
    <MenuItem value="deduction">Deduction</MenuItem>
    <MenuItem value="deposit">Deposit</MenuItem>
  </Select>
</FormControl>


          {/* Get Report Button */}
          <Button variant="contained" onClick={handleSearchClick}>
            Get Report
          </Button>

          {/* Export Button next to Get Report */}
          <Button variant="contained" color="primary" onClick={handleExportToExcel}>
            Export
          </Button>
        </Box>

        {/* Table */}
        <Container
          sx={{
            display: "flex",
            alignItems: "center",
            flexDirection: "column",
            minHeight: "calc(100vh - 64px)",
            margin: "auto",
            maxWidth: "lg",
            height: "auto",
          }}
        >
          <Grid container spacing={2} sx={{ padding: "10px" }}>
            <Grid item xs={12}>
              <Paper>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        {[
                          "S.No",
                          "DATE",
                          "STB NUMBER",
                          "TRANSACTION DETAILS",
                          "DEDUCTION",
                          "DEPOSIT",
                          "WALLET BALANCE",
                        ].map((text) => (
                          <TableCell
                            key={text}
                            sx={{
                              backgroundColor: "#ff8c00",
                              color: "#ffffff",
                              borderColor: "#ff8c00",
                            }}
                          >
                            {text}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Array.isArray(filteredData) && filteredData.length > 0 ? (
                        filteredData
                          .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                          .map((row, index) => (
                            <TableRow key={index}>
                              <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                             <TableCell>
  {moment.utc(row.No_of_days).format("YYYY-MM-DD HH:mm:ss")}
</TableCell>

                              <TableCell>{row.stb_number}</TableCell>
                              <TableCell>{row.No_of_STB}</TableCell>
                              <TableCell>{row.user_name}</TableCell>
                              <TableCell>{row.deposit}</TableCell>
                              <TableCell>{row.balance}</TableCell>
                            </TableRow>
                          ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={7} align="center">
                            No data available
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  rowsPerPageOptions={[10, 25, 50]}
                  component="div"
                  count={filteredData.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </main>
    </div>
  );
}
